// Helpers that build a ready-to-spread `filter` config for the DataTable
// header, picking between FilterType.DROPDOWN (static, from bulk) and
// FilterType.API_DROPDOWN (paginated + searchable) based on workflow_type.
//
// Call sites stay short:
//   filter: { id, label, searchKey, value, ...workflowIdsFilter(ctx) }
// without per-page conditionals.

import {
  getSnowflakeJobsFilterApps,
  getSnowflakeJobsFilterCustomTagValues,
  getSnowflakeJobsFilterTeams,
  getSnowflakeJobsFilterWorkflowIds,
  getSnowflakeJobsFilterWorkflowIdsWithLabels,
} from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { ExtractedOption, FilterType } from "@/components/tables/types";
import { ComboboxOption } from "@altimateai/lego";
import {
  SnowflakeJobsFilterLabelValuePage,
  SnowflakeJobsFilterPaginatedParams,
  SnowflakeJobsFilterStringPage,
  WorkflowType,
} from "@/pages/query/types";

export interface SnowflakeJobsFacetCtx {
  startDate: Date;
  endDate: Date;
  workflowType: WorkflowType;
}

interface PaginatedFilterCallParams {
  prefix?: string;
  page?: number;
  size?: number;
}

const PAGE_SIZE = 50;

const buildParams = (
  ctx: SnowflakeJobsFacetCtx,
  { prefix, page, size }: PaginatedFilterCallParams
): SnowflakeJobsFilterPaginatedParams => ({
  start_date: getDateByStartOfDayWithoutTimeoffset(ctx.startDate).toISOString(),
  end_date: getDateByEndOfDayWithoutTimeoffset(ctx.endDate)?.toISOString(),
  workflow_type: ctx.workflowType,
  search: prefix || undefined,
  page,
  size: size ?? PAGE_SIZE,
});

// MultiValueTableFilter erases TData via `unknown` at the boundary, so the
// helpers below produce unknown-typed function signatures and cast internally.
const stringPageExtractor = (data: unknown): ExtractedOption[] => {
  const page = data as SnowflakeJobsFilterStringPage | undefined;
  return page?.values?.map(v => ({ label: v, value: v })) ?? [];
};

const labelValuePageExtractor = (data: unknown): ExtractedOption[] => {
  const page = data as SnowflakeJobsFilterLabelValuePage | undefined;
  return page?.values?.map(v => ({ label: v.label, value: v.value })) ?? [];
};

export interface ApiDropdownFacetConfig {
  filterType: FilterType.API_DROPDOWN;
  apiFunction: (params: PaginatedFilterCallParams) => Promise<unknown>;
  extractorFunction: (data: unknown) => ExtractedOption[];
  seeMore: boolean;
  pageSize: number;
  // Stable identifier for the underlying endpoint+context — consumers like
  // FacetCombobox use this to detect facet swaps without comparing the
  // freshly-created apiFunction closures (which never compare equal).
  facetId: string;
}

export interface StaticDropdownFacetConfig {
  filterType: FilterType.DROPDOWN;
  options: ComboboxOption[];
}

export type FacetConfig = ApiDropdownFacetConfig | StaticDropdownFacetConfig;

// stringPageExtractor exported so duplicate definitions elsewhere (custom-tag
// hook, FacetCombobox) can reuse the same extractor.
export { stringPageExtractor };

// .getTime() avoids the locale/timezone churn that `String(Date)` introduces;
// otherwise an unmemoized `new Date()` from a parent would flip facetId
// every second and remount the lego Combobox, dropping in-flight pages.
const facetIdFor = (facetSlug: string, ctx: SnowflakeJobsFacetCtx) =>
  `${facetSlug}|${ctx.workflowType}|${ctx.startDate.getTime()}|${ctx.endDate.getTime()}`;

const stringFacet = (
  facetFn: (
    p: SnowflakeJobsFilterPaginatedParams
  ) => Promise<SnowflakeJobsFilterStringPage | undefined>,
  ctx: SnowflakeJobsFacetCtx,
  facetSlug: string
): ApiDropdownFacetConfig => ({
  filterType: FilterType.API_DROPDOWN,
  apiFunction: (params: PaginatedFilterCallParams) => facetFn(buildParams(ctx, params)),
  extractorFunction: stringPageExtractor,
  seeMore: true,
  pageSize: PAGE_SIZE,
  facetId: facetIdFor(facetSlug, ctx),
});

const labelValueFacet = (
  facetFn: (
    p: SnowflakeJobsFilterPaginatedParams
  ) => Promise<SnowflakeJobsFilterLabelValuePage | undefined>,
  ctx: SnowflakeJobsFacetCtx,
  facetSlug: string
): ApiDropdownFacetConfig => ({
  filterType: FilterType.API_DROPDOWN,
  apiFunction: (params: PaginatedFilterCallParams) => facetFn(buildParams(ctx, params)),
  extractorFunction: labelValuePageExtractor,
  seeMore: true,
  pageSize: PAGE_SIZE,
  facetId: facetIdFor(facetSlug, ctx),
});

const staticDropdown = (values: string[] | undefined): StaticDropdownFacetConfig => ({
  filterType: FilterType.DROPDOWN,
  options: (values ?? []).map(v => ({ label: v, value: v })),
});

// ---- per-facet entry points ------------------------------------------------

// Workflow IDs: always paginated; dispatches by workflow_type.
export const workflowIdsFilter = (ctx: SnowflakeJobsFacetCtx): ApiDropdownFacetConfig =>
  ctx.workflowType === WorkflowType.TABLEAU
    ? labelValueFacet(getSnowflakeJobsFilterWorkflowIdsWithLabels, ctx, "workflow_ids_with_labels")
    : stringFacet(getSnowflakeJobsFilterWorkflowIds, ctx, "workflow_ids");

// Teams: paginated for CUSTOM (max ~3k observed), static from bulk for TABLEAU.
// Pass the bulk slice as fallback for non-CUSTOM call sites.
export const teamsFilter = (
  ctx: SnowflakeJobsFacetCtx,
  bulkTeams?: string[]
): ApiDropdownFacetConfig | StaticDropdownFacetConfig =>
  ctx.workflowType === WorkflowType.CUSTOM
    ? stringFacet(getSnowflakeJobsFilterTeams, ctx, "teams")
    : staticDropdown(bulkTeams);

// Apps: paginated for CUSTOM (max ~17k observed), static from bulk for TABLEAU.
export const appsFilter = (
  ctx: SnowflakeJobsFacetCtx,
  bulkApps?: string[]
): ApiDropdownFacetConfig | StaticDropdownFacetConfig =>
  ctx.workflowType === WorkflowType.CUSTOM
    ? stringFacet(getSnowflakeJobsFilterApps, ctx, "apps")
    : staticDropdown(bulkApps);

// Custom tag values: always paginated; one config per configured tag_key.
// (tag_key list comes from bulk.custom_tag_keys, capped at 15.)
export const customTagValuesFilter = (
  tagKey: string,
  ctx: SnowflakeJobsFacetCtx
): ApiDropdownFacetConfig => ({
  filterType: FilterType.API_DROPDOWN,
  apiFunction: (params: PaginatedFilterCallParams) =>
    getSnowflakeJobsFilterCustomTagValues(tagKey, buildParams(ctx, params)),
  extractorFunction: stringPageExtractor,
  seeMore: true,
  pageSize: PAGE_SIZE,
  facetId: facetIdFor(`custom_tags/${tagKey}`, ctx),
});
